/**
 * deliverySchedule.js (web)
 * ---------------------------------------------------------------------------
 * Delivery timing for every web checkout: Express, or a 30 minute window picked
 * from GET /api/delivery-slots. Pure helpers only (no React, no redux, no
 * network) so every place-order screen reads, sends and shows the schedule the
 * same way. Mirrors Lusuku_Expo_Clean/app/utils/deliverySchedule.js.
 *
 * CONTRACT WITH THE SERVER (services/orders/deliverySlots.js)
 *   Order body:  deliverySchedule: { type: "EXPRESS" }
 *                deliverySchedule: { type: "SCHEDULED", windowStart, windowEnd }
 *   A window must start at least 45 minutes after the order is created,
 *   otherwise order create answers 400 DELIVERY_SLOT_INVALID.
 *   A vendor that is closed now but accepts scheduled orders answers an Express
 *   order with 409 SCHEDULED_ORDERS_ONLY.
 *   Stored on the order: deliverySchedule "Express" | "Scheduled" and
 *   scheduledDelivery { windowStart, windowEnd, timezone, label }.
 *
 * The server is rolled out alongside this file, so every newer field
 * (expressAvailable, schedulingAvailable, scheduledOrdersOnly, label) is
 * optional here and its absence keeps today's behaviour.
 */

export const DELIVERY_TYPE = Object.freeze({
  EXPRESS: "EXPRESS",
  SCHEDULED: "SCHEDULED",
});

export const DELIVERY_SLOT_INVALID = "DELIVERY_SLOT_INVALID";
export const SCHEDULED_ORDERS_ONLY = "SCHEDULED_ORDERS_ONLY";

// Mirrors ACCEPT_LEAD_MINUTES on the server.
export const SLOT_ACCEPT_LEAD_MINUTES = 45;

export const EXPRESS_SCHEDULE = Object.freeze({ type: DELIVERY_TYPE.EXPRESS });

export const SAME_FEE_NOTE = "Both options cost the same delivery fee.";
export const SCHEDULED_ONLY_NOTICE = "Closed now. Order ahead for later.";
export const EXPRESS_CLOSED_HINT = "Closed right now";
// The pill on a vendor card that is closed now and takes orders for later.
export const ORDER_AHEAD_LABEL = "Order ahead";
// Where the picked schedule waits for the success page. sessionStorage, so it
// dies with the tab and never leaks into the next checkout on another device.
const PICKED_SCHEDULE_KEY = "lusuku.deliverySchedule.picked";

const validInstant = (value) => {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value !== "string" || !value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Any value to a well formed schedule. A scheduled value without a readable
 * window falls back to Express rather than sending half a schedule.
 */
export function normalizeDeliverySchedule(value) {
  if (!value || typeof value !== "object") return EXPRESS_SCHEDULE;
  if (String(value.type || "").toUpperCase() !== DELIVERY_TYPE.SCHEDULED) {
    return EXPRESS_SCHEDULE;
  }
  const startMs = validInstant(value.windowStart);
  const endMs = validInstant(value.windowEnd);
  if (startMs === null || endMs === null || endMs <= startMs) {
    return EXPRESS_SCHEDULE;
  }
  return {
    type: DELIVERY_TYPE.SCHEDULED,
    windowStart: value.windowStart,
    windowEnd: value.windowEnd,
    dayLabel: value.dayLabel || "",
    windowLabel: value.windowLabel || "",
  };
}

export function isScheduledDelivery(value) {
  return normalizeDeliverySchedule(value).type === DELIVERY_TYPE.SCHEDULED;
}

/** The schedule for a window chosen in the picker. */
export function buildScheduledDelivery(day, window) {
  return normalizeDeliverySchedule({
    type: DELIVERY_TYPE.SCHEDULED,
    windowStart: window?.start,
    windowEnd: window?.end,
    dayLabel: day?.label,
    windowLabel: window?.label,
  });
}

/** Exactly what the order-create body carries. */
export function toOrderDeliverySchedule(value) {
  const schedule = normalizeDeliverySchedule(value);
  if (schedule.type !== DELIVERY_TYPE.SCHEDULED) {
    return { type: DELIVERY_TYPE.EXPRESS };
  }
  return {
    type: DELIVERY_TYPE.SCHEDULED,
    windowStart: schedule.windowStart,
    windowEnd: schedule.windowEnd,
  };
}

/**
 * True when a scheduled window is now too close (or past) for the server to
 * accept. Checked right before the order is created, because a customer can sit
 * on the confirm page long after picking.
 */
export function isDeliveryScheduleStale(value, now = Date.now()) {
  const schedule = normalizeDeliverySchedule(value);
  if (schedule.type !== DELIVERY_TYPE.SCHEDULED) return false;
  return (
    Date.parse(schedule.windowStart) <
    now + SLOT_ACCEPT_LEAD_MINUTES * 60 * 1000
  );
}

/** Keep a pick only while it is still one of the listed windows. */
export function findListedWindow(days, value) {
  const schedule = normalizeDeliverySchedule(value);
  if (schedule.type !== DELIVERY_TYPE.SCHEDULED || !Array.isArray(days)) {
    return null;
  }
  for (const day of days) {
    const window = (day?.windows || []).find(
      (w) => w.start === schedule.windowStart
    );
    if (window) return { day, window };
  }
  return null;
}

/** Order-create failures that mean "pick another delivery time". */
export function isScheduleRejectionCode(code) {
  return code === DELIVERY_SLOT_INVALID || code === SCHEDULED_ORDERS_ONLY;
}

/**
 * GET /api/delivery-slots body to the shape the picker renders. Returns null
 * for an unreadable body. Fields an older server does not send default to
 * today's behaviour: Express available, scheduling available.
 */
export function normalizeDeliverySlots(data) {
  if (!data || typeof data !== "object" || !Array.isArray(data.days)) {
    return null;
  }
  const days = data.days.map((day) => ({
    date: day?.date,
    label: day?.label,
    windows: Array.isArray(day?.windows) ? day.windows : [],
  }));
  return {
    timezone: data.timezone || null,
    days,
    hasWindows: days.some((day) => day.windows.length > 0),
    openNow: typeof data.openNow === "boolean" ? data.openNow : null,
    acceptsScheduledOrders: data.acceptsScheduledOrders === true,
    expressAvailable: data.expressAvailable !== false,
    schedulingAvailable: data.schedulingAvailable !== false,
    message: typeof data.message === "string" ? data.message : null,
  };
}

/* ---------------------------------------------------------------------------
 * Closed vendors. ONE decision for every gate on the site (list cards, shop
 * pages, carts, shipping and checkout), read from an open-status payload or a
 * vendor row's `openStatus`.
 * ------------------------------------------------------------------------- */

export const ORDERING_MODE = Object.freeze({
  OPEN: "OPEN", // trading now: Express or Scheduled
  SCHEDULED_ONLY: "SCHEDULED_ONLY", // closed now, takes orders for later
  CLOSED: "CLOSED", // closed and not taking scheduled orders
  UNKNOWN: "UNKNOWN", // no verdict (nothing published, or no payload)
});

export function resolveOrderingMode(status) {
  if (!status || typeof status !== "object") return ORDERING_MODE.UNKNOWN;
  if (status.isActive === false) return ORDERING_MODE.CLOSED;
  const open =
    typeof status.available === "boolean"
      ? status.available
      : typeof status.isOpen === "boolean"
        ? status.isOpen
        : null;
  if (open === true) return ORDERING_MODE.OPEN;
  if (status.scheduledOrdersOnly === true) return ORDERING_MODE.SCHEDULED_ONLY;
  if (open === false) return ORDERING_MODE.CLOSED;
  return ORDERING_MODE.UNKNOWN;
}

/** May the customer browse to cart and checkout with this status? */
export function orderingModeAllowsOrdering(mode) {
  return mode !== ORDERING_MODE.CLOSED;
}

/* ---------------------------------------------------------------------------
 * Which seller's hours bound the picker for a checkout flow. Each place-order
 * screen passes the same vendor id it quotes the fee and creates the order
 * with, so the windows offered are the windows order create validates.
 * ------------------------------------------------------------------------- */

const FLOW_VENDOR_TYPE = {
  restaurant: "restaurant",
  rw: "retailer",
  retailer: "retailer",
  wholesaler: "retailer",
  wholesale: "retailer",
  brand: "brand",
  healthcare: "healthcare",
  pharmacy: "healthcare",
};

/**
 * @returns {{vendorType:string, vendorId?:string, outletId?:string}|null}
 *          null when this checkout cannot be scheduled yet (no vendor id).
 */
export function resolveSlotQuery({ flow, vendorId, outletId } = {}) {
  const key = String(flow || "").toLowerCase();
  if (key === "inhouse") return { vendorType: "inhouse" };
  const vendorType = FLOW_VENDOR_TYPE[key];
  if (!vendorType || !vendorId) return null;
  return {
    vendorType,
    vendorId: String(vendorId),
    ...(outletId ? { outletId: String(outletId) } : {}),
  };
}

/* ---------------------------------------------------------------------------
 * Customer order history and detail.
 * ------------------------------------------------------------------------- */

const formatWindowFallback = (startMs, endMs, timezone) => {
  const build = (timeZone) => {
    const tz = timeZone ? { timeZone } : {};
    const day = new Date(startMs).toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      ...tz,
    });
    const clock = (ms) =>
      new Date(ms).toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        ...tz,
      });
    return `${day}, ${clock(startMs)} to ${clock(endMs)}`;
  };
  try {
    return build(timezone || undefined);
  } catch (_) {
    // An unknown timezone name throws a RangeError; fall back to device time.
    return build(undefined);
  }
};

/* ---------------------------------------------------------------------------
 * The pick, carried from checkout to the success page.
 *
 * The created order is the source of truth, but the order body a payment page
 * reads back can be an older projection without `scheduledDelivery`. The
 * customer just chose a delivery window and must see it confirmed, so checkout
 * remembers the pick and the success page falls back to it FOR THAT ORDER ONLY.
 * ------------------------------------------------------------------------- */

/** The remembered pick, only when it belongs to this order. Pure. */
export function matchRememberedSchedule(order, remembered) {
  if (!remembered || typeof remembered !== "object") return null;
  const rememberedId = remembered.orderId ? String(remembered.orderId) : null;
  const orderId = order?._id ? String(order._id) : null;
  // BOTH ids must be present and equal. A pick stored without an order id, or
  // an order body read back without one, is not evidence that this window
  // belongs to THIS order, and a confirmation page that falls back on a
  // missing id prints the PREVIOUS order's delivery time as if it were this
  // one. Showing nothing is the only honest answer we can give there.
  if (!rememberedId || !orderId || rememberedId !== orderId) return null;
  const schedule = normalizeDeliverySchedule(remembered.schedule);
  return schedule.type === DELIVERY_TYPE.SCHEDULED ? schedule : null;
}

/**
 * The delivery time to show for a just-created order: what the order itself
 * says, else the window the customer picked for it. Pure; `remembered` comes
 * from readPickedSchedule().
 */
export function describeCreatedOrderDeliveryTime(order, remembered) {
  const fromOrder = describeOrderDeliveryTime(order);
  if (fromOrder) return fromOrder;
  const schedule = matchRememberedSchedule(order, remembered);
  if (!schedule) return null;
  const labelled = [schedule.dayLabel, schedule.windowLabel]
    .filter(Boolean)
    .join(", ");
  if (labelled) return { title: "Scheduled delivery", detail: labelled };
  return describeOrderDeliveryTime({
    deliverySchedule: "Scheduled",
    scheduledDelivery: {
      windowStart: schedule.windowStart,
      windowEnd: schedule.windowEnd,
    },
  });
}

/**
 * The created order's id in a checkout response, wherever that surface puts it:
 * the brand quote accept answers { data: { order } }, the parcel create answers
 * the order itself, and the order thunks answer { order } or the order.
 */
export function createdOrderIdIn(body) {
  return body?.data?.order?._id || body?.order?._id || body?._id || null;
}

/** Checkout remembers the pick for the order it just created. Best effort. */
export function rememberPickedSchedule(schedule, orderId) {
  try {
    const normalized = normalizeDeliverySchedule(schedule);
    if (normalized.type !== DELIVERY_TYPE.SCHEDULED) {
      window.sessionStorage.removeItem(PICKED_SCHEDULE_KEY);
      return;
    }
    window.sessionStorage.setItem(
      PICKED_SCHEDULE_KEY,
      JSON.stringify({ schedule: normalized, orderId: orderId || null })
    );
  } catch (_) {
    // Private mode, blocked storage: the order itself still carries the window.
  }
}

/** What checkout remembered, or null. */
export function readPickedSchedule() {
  try {
    const raw = window.sessionStorage.getItem(PICKED_SCHEDULE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/**
 * What an order says about its delivery time, or null for nothing to show.
 * Scheduled orders read "Scheduled delivery" with the server's label; an order
 * from before labels existed formats its window in the device locale.
 *
 * @returns {{title:string, detail:string}|null}
 */
export function describeOrderDeliveryTime(order) {
  if (!order || order.deliverySchedule !== "Scheduled") return null;
  const slot = order.scheduledDelivery;
  if (!slot || typeof slot !== "object") return null;
  if (typeof slot.label === "string" && slot.label.trim()) {
    return { title: "Scheduled delivery", detail: slot.label.trim() };
  }
  const startMs = validInstant(slot.windowStart);
  const endMs = validInstant(slot.windowEnd);
  if (startMs === null || endMs === null) return null;
  return {
    title: "Scheduled delivery",
    detail: formatWindowFallback(startMs, endMs, slot.timezone),
  };
}

// Named before export: CRA's import/no-anonymous-default-export fails the
// Netlify build (CI=true).
const deliverySchedule = {
  DELIVERY_TYPE,
  EXPRESS_SCHEDULE,
  ORDERING_MODE,
  normalizeDeliverySchedule,
  isScheduledDelivery,
  buildScheduledDelivery,
  toOrderDeliverySchedule,
  isDeliveryScheduleStale,
  findListedWindow,
  isScheduleRejectionCode,
  normalizeDeliverySlots,
  resolveOrderingMode,
  orderingModeAllowsOrdering,
  resolveSlotQuery,
  describeOrderDeliveryTime,
  describeCreatedOrderDeliveryTime,
  matchRememberedSchedule,
  createdOrderIdIn,
  rememberPickedSchedule,
  readPickedSchedule,
};

export default deliverySchedule;
